import React from 'react';

import { Select, Switch } from 'antd';

import { TextCustomizationSettings } from '../../@types/contract';

interface TextCustomizationProps {
  onSettingsChange: (settings: TextCustomizationSettings) => void;
  settings?: TextCustomizationSettings;
}

const TextCustomization: React.FC<TextCustomizationProps> = ({ onSettingsChange, settings }) => {
  const fontSizeOptions = [
    { value: '8px', label: '8px' },
    { value: '9px', label: '9px' },
    { value: '10px', label: '10px' },
    { value: '11px', label: '11px' },
    { value: '12px', label: '12px' },
    { value: '13px', label: '13px' },
    { value: '14px', label: '14px' },
    { value: '15px', label: '15px' },
    { value: '16px', label: '16px' },
    { value: '17px', label: '17px' },
    { value: '18px', label: '18px' },
    { value: '19px', label: '19px' },
    { value: '20px', label: '20px' },
  ];

  const fontNameOptions = [
    { value: '', label: 'font style' },
    { value: 'Algerian', label: 'Algerian' },
    { value: 'Arial', label: 'Arial' },
    { value: 'Calibri', label: 'Calibri' },
    { value: 'Cambria', label: 'Cambria' },
    { value: 'Cambria Math', label: 'Cambria Math' },
    { value: 'Candara', label: 'Candara' },
    { value: 'Courier New', label: 'Courier New' },
    { value: 'Georgia', label: 'Georgia' }, // corrected spelling from "Gerogia"
    { value: 'Impact', label: 'Impact' },
    { value: 'Segoe Print', label: 'Segoe Print' },
    { value: 'Segoe Script', label: 'Segoe Script' },
    { value: 'Segoe UI', label: 'Segoe UI' },
    { value: 'Symbol', label: 'Symbol' },
    { value: 'Times New Roman', label: 'Times New Roman' },
  ];

  const handleSettingChange = (key: keyof TextCustomizationSettings, value: unknown) => {
    const newSettings = {
      ...settings,
      [key]: value,
    };
    onSettingsChange(newSettings);
  };

  return (
    <div className='space-y-6 border rounded-lg p-3 bg-white'>
      <h4 className='text-sm mb-2'>Clause formatting:</h4>
      <div className='flex items-center space-x-4 '>
        <Select
          value={settings?.fontName}
          onChange={(value) => handleSettingChange('fontName', value)}
          className='w-24 px-2 py-1'
          size='large'
        >
          {fontNameOptions.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>

        <Select
          value={settings?.fontSize}
          onChange={(value) => handleSettingChange('fontSize', value)}
          className='w-24'
          size='middle'
        >
          {fontSizeOptions.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>

        <div className='h-6 w-px bg-gray-300'></div>
        <div className='flex space-x-1'>
          <button
            className={`px-3 py-1 border rounded hover:bg-gray-50 transition-colors ${
              settings?.isBold ? 'bg-blue-50 border-blue-500 text-blue-600' : 'bg-white'
            }`}
            onClick={() => handleSettingChange('isBold', !settings?.isBold)}
          >
            <span className='font-bold'>B</span>
          </button>
          <button
            className={`px-3 py-1 border rounded hover:bg-gray-50 transition-colors ${
              settings?.isItalic ? 'bg-blue-50 border-blue-500 text-blue-600' : 'bg-white'
            }`}
            onClick={() => handleSettingChange('isItalic', !settings?.isItalic)}
          >
            <span className='italic'>I</span>
          </button>
          <button
            className={`px-3 py-1 border rounded hover:bg-gray-50 transition-colors ${
              settings?.isUnderline ? 'bg-blue-50 border-blue-500 text-blue-600' : 'bg-white'
            }`}
            onClick={() => handleSettingChange('isUnderline', !settings?.isUnderline)}
          >
            <span className='underline'>U</span>
          </button>
        </div>

        <div className='h-6 w-px bg-gray-300'></div>

        <div className='flex items-center space-x-2'>
          <div
            className='w-8 h-8 rounded border cursor-pointer'
            style={{ backgroundColor: settings?.fontColor }}
          />
          <input
            type='text'
            value={settings?.fontColor}
            onChange={(e) => {
              const hexInputRegex = /^#[A-Fa-f0-9]{0,6}$/;
              if (hexInputRegex.test(e.target.value)) {
                handleSettingChange('fontColor', e.target.value);
              }
            }}
            onBlur={(e) => {
              const hexColorRegex = /^#[A-Fa-f0-9]{6}$/;
              if (!hexColorRegex.test(e.target.value)) {
                handleSettingChange('fontColor', '#000000');
              }
            }}
            placeholder='#000000'
            className='w-24 px-2 py-1 border rounded text-sm'
            maxLength={7}
          />
        </div>

        <div className='h-6 w-px bg-gray-300'></div>

        <div className='flex items-center space-x-2'>
          <input
            type='checkbox'
            id='underlineHeader'
            checked={settings?.underlineHeader}
            onChange={(e) => handleSettingChange('underlineHeader', e.target.checked)}
            className='form-checkbox h-4 w-4 text-blue-600 rounded'
          />
          <label htmlFor='underlineHeader' className='text-sm select-none'>
            Underline Header
          </label>
        </div>
      </div>
      <div className='flex items-center space-x-4 '>
        <Switch
          id='isLevelClauseNumbering'
          checked={settings?.isLevelClauseNumbering}
          className={'col-span-3'}
          onChange={(checked) => handleSettingChange('isLevelClauseNumbering', checked)}
        />
        <label htmlFor='isLevelClauseNumbering' className='text-sm select-none'>
          Include Clause number in enumerations
        </label>
      </div>
      <div className=''>
        <h4 className='text-sm text-gray-600 mb-2'>Preview:</h4>
        <p
          className={`p-3 bg-white border rounded ${settings?.isBold ? 'font-bold' : ''} ${
            settings?.isItalic ? 'italic' : ''
          } ${settings?.isUnderline ? 'underline' : ''}`}
          style={{
            fontSize: settings?.fontSize,
            color: settings?.fontColor,
          }}
        >
          Sample Text Preview
        </p>
      </div>
    </div>
  );
};

export default TextCustomization;
